import { useQuery } from '@tanstack/react-query';
import { Package, Users2, AlertCircle, Wrench, ArchiveRestore, ScanLine } from 'lucide-react';
import { Link } from 'react-router-dom';

import { getInventarioDashboard } from '@/services/api/inventario.api';
import { KpiCard } from '@/components/dashboard/KpiCard';
import { SemaforoSummary } from '@/components/dashboard/SemaforoSummary';
import { InventarioBreakdown } from '@/components/dashboard/InventarioBreakdown';
import { AccesosResumen } from '@/components/dashboard/AccesosResumen';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/lib/utils';
import type { Rol } from '@/types/auth';

export function DashboardPage() {
  const { user } = useAuthStore();
  const rol: Rol = user?.rol ?? 'GUARDIA';

  // ── Visibilidad por rol ──────────────────────────────────────────────────
  const verAccesos = rol === 'ADMIN' || rol === 'SOPORTE';
  const verInventario = rol === 'ADMIN' || rol === 'COMPRAS';
  const verMantenimiento = rol === 'ADMIN' || rol === 'SOPORTE';

  // COMPRAS solo ve su propio inventario (sin GLPI). El backend lo fuerza, pero
  // también lo pedimos explícito para cachear correcto y mostrar el alcance.
  const esCompras = rol === 'COMPRAS';
  const origenScope = esCompras ? 'COMPRAS' : undefined;

  // El dashboard de inventario alimenta tanto los KPIs de inventario como el
  // semáforo de mantenimiento, así que se consulta si cualquiera de los dos aplica.
  const { data, isLoading } = useQuery({
    queryKey: ['inventario-dashboard', origenScope ?? 'ALL'],
    queryFn: () => getInventarioDashboard(origenScope),
    enabled: verInventario || verMantenimiento,
    refetchInterval: 60_000,
  });

  const total      = data?.total_activos     ?? 0;
  const asignados  = data?.total_asignados   ?? 0;
  const bodega     = data?.total_bodega      ?? 0;
  const vencidos   = data?.mantenimientos.vencidos ?? 0;
  const reparacion = data?.total_reparacion  ?? 0;

  const dualBlocks = verMantenimiento && verInventario;
  const sinContenido = !verAccesos && !verInventario && !verMantenimiento;

  return (
    <div className="space-y-8">
      {/* ── Saludo y CTA contextuales ───────────────────────────────────── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-widest text-primary-600">{greeting()}</p>
          <h2 className="text-2xl font-bold tracking-tight text-foreground">
            Hola, {user?.username?.split('.')[0] ?? 'usuario'} 👋
          </h2>
          <p className="text-sm text-muted-foreground">
            Aquí tienes un vistazo rápido al estado del aplicativo.
          </p>
        </div>
        <div className="flex gap-2">
          {verMantenimiento && (
            <Button variant="outline" asChild>
              <Link to="/mantenimiento"><Wrench className="h-4 w-4" />Mantenimientos</Link>
            </Button>
          )}
          {verAccesos && (
            <Button variant="outline" asChild>
              <Link to="/accesos"><ScanLine className="h-4 w-4" />Accesos</Link>
            </Button>
          )}
          {verInventario && (
            <Button asChild>
              <Link to="/inventario"><Package className="h-4 w-4" />Inventario</Link>
            </Button>
          )}
        </div>
      </div>

      {/* ── Accesos (ADMIN, SOPORTE) ────────────────────────────────────── */}
      {verAccesos && <AccesosResumen />}

      {/* ── KPIs de inventario (ADMIN, COMPRAS) ─────────────────────────── */}
      {verInventario && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label={esCompras ? 'Activos de compras' : 'Total activos'} value={total} icon={Package} tone="primary" loading={isLoading} />
          <KpiCard label="Asignados" value={asignados} icon={Users2} tone="success" loading={isLoading} />
          <KpiCard label="En bodega" value={bodega} icon={ArchiveRestore} tone={bodega <= 3 ? 'danger' : 'neutral'} loading={isLoading} />
          {esCompras ? (
            // Para COMPRAS evitamos "Mtos. vencidos" (señal de equipos GLPI) y
            // mostramos una métrica propia de su inventario.
            <KpiCard label="En reparación" value={reparacion} icon={Wrench} tone={reparacion > 0 ? 'warning' : 'neutral'} loading={isLoading} />
          ) : (
            <KpiCard label="Mtos. vencidos" value={vencidos} icon={AlertCircle} tone={vencidos > 0 ? 'danger' : 'success'} loading={isLoading} />
          )}
        </div>
      )}

      {/* ── Alerta de stock bajo (ADMIN, COMPRAS) ───────────────────────── */}
      {verInventario && data?.stock_bajo && (
        <Card className="border-amber-300 bg-amber-50/60">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertCircle className="h-5 w-5 text-amber-700 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">Stock disponible bajo el umbral</p>
              <p className="text-xs text-amber-800">
                Solo {bodega} activos en bodega. Considera reabastecer las categorías críticas.
              </p>
            </div>
            <Button size="sm" variant="outline" asChild className="border-amber-300 text-amber-900 hover:bg-amber-100">
              <Link to="/inventario">Revisar</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── Semáforo (mantenimiento) + Inventario por tipo ──────────────── */}
      {(verMantenimiento || verInventario) && (
        <div className={cn('grid grid-cols-1 gap-4', dualBlocks && 'lg:grid-cols-2')}>
          {verMantenimiento && (
            <SemaforoSummary
              data={data?.mantenimientos ?? { al_dia: 0, proximos: 0, vencidos: 0, sin_programar: 0 }}
              loading={isLoading}
            />
          )}
          {verInventario && (
            <InventarioBreakdown porTipo={data?.por_tipo ?? []} loading={isLoading} />
          )}
        </div>
      )}

      {/* ── Atajos rápidos (filtrados por rol) ──────────────────────────── */}
      {!sinContenido && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Accesos rápidos</CardTitle>
            <CardDescription>Las acciones más usadas del panel</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {verMantenimiento && <QuickAction to="/mantenimiento?semaforo=DANGER" label="Mtos. vencidos" icon={AlertCircle} tone="danger" />}
              {verMantenimiento && <QuickAction to="/mantenimiento?semaforo=WARN" label="Próximos a vencer" icon={Wrench} tone="warning" />}
              {verAccesos && <QuickAction to="/accesos" label="Movimientos hoy" icon={ScanLine} tone="primary" />}
              {verAccesos && <QuickAction to="/colaboradores" label="Buscar colaborador" icon={Users2} tone="neutral" />}
              {verInventario && <QuickAction to="/inventario" label="Gestionar inventario" icon={Package} tone="success" />}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Fallback para roles sin módulos asignados ───────────────────── */}
      {sinContenido && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No tienes módulos asignados a tu rol. Contacta a un administrador.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function QuickAction({
  to, label, icon: Icon, tone,
}: {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  tone: 'primary' | 'success' | 'warning' | 'danger' | 'neutral';
}) {
  const TONE = {
    primary: 'hover:border-primary-300 hover:bg-primary-50 [&_svg]:text-primary-600',
    success: 'hover:border-emerald-300 hover:bg-emerald-50 [&_svg]:text-emerald-600',
    warning: 'hover:border-amber-300 hover:bg-amber-50 [&_svg]:text-amber-700',
    danger:  'hover:border-red-300 hover:bg-red-50 [&_svg]:text-red-600',
    neutral: 'hover:border-slate-300 hover:bg-slate-50 [&_svg]:text-slate-700',
  };
  return (
    <Link
      to={to}
      className={`group flex flex-col items-start gap-2 rounded-lg border bg-card p-4 transition-colors ${TONE[tone]}`}
    >
      <Icon className="h-5 w-5 transition-transform group-hover:scale-110" />
      <span className="text-sm font-medium text-foreground">{label}</span>
    </Link>
  );
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Buenos días';
  if (h < 19) return 'Buenas tardes';
  return 'Buenas noches';
}
